import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { adminApiCalls } from '../../../apiCalls/adminApiCalls';
import { imagePoolApiCalls } from '../../../apiCalls/imagePoolApiCalls';
import { videoPoolApiCalls } from '../../../apiCalls/videoPoolApiCalls';
import { IMAGE_VIDEO_POOL_QUERY_KEYS, SERVER_ERROR_CODES } from '../../../constants/constants';
import { RESOURCE_POOL_TYPE, ServerError } from '../../../typesAndInterfaces/typesAndInterfaces';
import { MB_formDataCreator } from '@/utils/general/form-data-creator';
import { BulkUploadConfig, BulkUploadItem, BULK_UPLOAD_IMAGE_CONCURRENCY, BULK_UPLOAD_ITEM_STATUS, BULK_UPLOAD_VIDEO_CONCURRENCY } from './bulkUploadTypes';

let bulkUploadItemIdCounter = 0;
export const nextBulkUploadItemId = () => `bulk-upload-item-${++bulkUploadItemIdCounter}`;

/**
 * Owns the review-list items and runs the upload queue with limited concurrency
 * (videos are capped lower than images to avoid piling onto the transcoding pipeline).
 * Each file goes through the same single-upload endpoint as the existing one-at-a-time flow.
 */
const useBulkUploadQueue = ({ resourcePoolType, folderId }: BulkUploadConfig) => {
  const [items, setItems] = useState<BulkUploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [hasUploadRun, setHasUploadRun] = useState(false);
  const queryClient = useQueryClient();
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const isImagePool = resourcePoolType === RESOURCE_POOL_TYPE.Image;

  const updateItem = useCallback((id: string, patch: Partial<BulkUploadItem> | ((item: BulkUploadItem) => Partial<BulkUploadItem>)) => {
    setItems(prevItems => prevItems.map(item => (item.id === id ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch) } : item)));
  }, []);

  const addItems = useCallback((newItems: BulkUploadItem[]) => {
    setItems(prevItems => [...prevItems, ...newItems]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  }, []);

  const reset = useCallback(() => {
    setItems([]);
    setIsUploading(false);
    setHasUploadRun(false);
  }, []);

  const uploadOne = useCallback(
    async (item: BulkUploadItem): Promise<boolean> => {
      updateItem(item.id, { status: BULK_UPLOAD_ITEM_STATUS.uploading, progress: 0, errorMessage: undefined });

      const poolType = isImagePool ? 'image' : 'video';
      const trimmedName = item.name.trim();

      // Same pre-upload duplicate guard as the single-upload flow. Non-fatal if the
      // check call itself fails; a confirmed duplicate fails just this item so the
      // admin can rename it and retry without touching the rest of the batch.
      let isDuplicate = false;
      try {
        isDuplicate = await adminApiCalls.checkPoolDuplicate(poolType, trimmedName);
      } catch {
        isDuplicate = false;
      }
      if (isDuplicate) {
        updateItem(item.id, {
          status: BULK_UPLOAD_ITEM_STATUS.error,
          isDuplicateOnServer: true,
          errorMessage: `A ${poolType} named "${trimmedName}" already exists. Rename and retry.`,
        });
        return false;
      }

      try {
        const formData = await MB_formDataCreator.createFormData({
          dataForFile: [{ key: poolType, blob: item.file.blob, uri: item.file.uri }],
        });
        formData.append('name', trimmedName);
        if (folderId) {
          formData.append('folderId', folderId);
        }
        for (const tag of item.tags) {
          formData.append('tags[]', tag);
        }

        const uploadProgress = (percentCompleted: number) => updateItem(item.id, { progress: percentCompleted });

        if (isImagePool) {
          await imagePoolApiCalls.uploadImage({ formData, uploadProgress });
        } else {
          await videoPoolApiCalls.uploadVideo({ formData, uploadProgress });
        }

        updateItem(item.id, { status: BULK_UPLOAD_ITEM_STATUS.success, progress: 100 });
        return true;
      } catch (error) {
        const serverError = error as ServerError;
        const knownError = serverError?.errorCode === SERVER_ERROR_CODES.LIMIT_FILE_SIZE || serverError?.errorCode === SERVER_ERROR_CODES.IMAGE_WITH_GIVEN_NAME_ALREADY_EXISTS;
        updateItem(item.id, {
          status: BULK_UPLOAD_ITEM_STATUS.error,
          errorMessage: knownError && serverError.message ? serverError.message : 'Upload failed. Tap retry to try again.',
        });
        return false;
      }
    },
    [folderId, isImagePool, updateItem]
  );

  /**
   * Uploads every item in `ready` or `error` state. Items already uploaded are skipped,
   * so calling this again after failures acts as "retry all failed".
   */
  const startUpload = useCallback(
    async (itemIds?: string[]) => {
      const pendingIds = itemsRef.current
        .filter(item => (item.status === BULK_UPLOAD_ITEM_STATUS.ready || item.status === BULK_UPLOAD_ITEM_STATUS.error) && (!itemIds || itemIds.includes(item.id)))
        .map(item => item.id);
      if (pendingIds.length === 0) {
        return;
      }

      setIsUploading(true);
      setHasUploadRun(true);

      const concurrency = isImagePool ? BULK_UPLOAD_IMAGE_CONCURRENCY : BULK_UPLOAD_VIDEO_CONCURRENCY;
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < pendingIds.length) {
          const id = pendingIds[nextIndex++];
          const item = itemsRef.current.find(candidate => candidate.id === id);
          if (item) {
            await uploadOne(item);
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(concurrency, pendingIds.length) }, worker));

      setIsUploading(false);
      queryClient.invalidateQueries([isImagePool ? IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages : IMAGE_VIDEO_POOL_QUERY_KEYS.getVideoPoolVideos]);
    },
    [isImagePool, queryClient, uploadOne]
  );

  const retryItem = useCallback((id: string) => startUpload([id]), [startUpload]);

  return { items, addItems, updateItem, removeItem, reset, startUpload, retryItem, isUploading, hasUploadRun };
};

export { useBulkUploadQueue };
